"use client";

import Image from "next/image";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { cn } from "@/lib/utils";
import { getBlogPostImage } from "@/lib/blog-images";
import { useLanguage } from "@/contexts/LanguageContext";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { reportWarning } from "@/lib/observability/report-error";
import { ArrowRight, Clock } from "lucide-react";
import { formatDate as safeFormatDate } from "@/lib/utils/format";

interface ArticleCardProps {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  tags?: string[];
  variant?: "default" | "compact";
  className?: string;
}

function formatDate(dateString: string, locale: string): string {
  try {
    return safeFormatDate(dateString, dateString, locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    reportWarning(error, {
      source: "components.ArticleCard",
      feature: "blog",
      action: "formatDate",
    });
    return dateString;
  }
}

export function ArticleCard({
  slug,
  title,
  description,
  date,
  readTime,
  tags,
  variant = "default",
  className,
}: ArticleCardProps) {
  const { language } = useLanguage();
  const { t } = useJsonTranslation("Blog");
  const imageUrl = getBlogPostImage(slug);

  if (variant === "compact") {
    return (
      <Link
        href={`/blog/${slug}`}
        className="group block min-w-0 overflow-visible whitespace-normal"
      >
        <article
          className={cn(
            "flex h-full flex-col overflow-hidden rounded-xl",
            "border-border/50 bg-card border shadow-sm",
            "transition-all duration-300",
            "hover:border-border dark:bg-foreground/[0.02] dark:hover:bg-foreground/[0.04] hover:bg-muted hover:shadow-lg hover:shadow-black/20 dark:shadow-none",
            className
          )}
        >
          <div className="relative aspect-[16/9] overflow-hidden rounded-t-xl">
            <Image
              src={imageUrl}
              alt={title}
              fill
              className="blog-card-image object-cover"
              unoptimized
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>
          <div className="flex flex-grow flex-col p-5">
            {tags?.[0] && (
              <span className="text-leaf-600 dark:text-leaf-400/80 mb-2 text-[11px] font-semibold tracking-widest uppercase">
                {tags[0]}
              </span>
            )}
            <h3
              className="text-foreground group-hover:text-leaf-600 dark:group-hover:text-leaf-300 mb-2 text-[0.95rem] font-bold transition-colors"
              style={{
                display: "block",
                overflow: "visible",
                textOverflow: "unset",
                whiteSpace: "normal",
                wordBreak: "normal",
                overflowWrap: "anywhere",
                lineHeight: "1.4",
              }}
            >
              {title}
            </h3>
            <p className="text-muted-foreground/70 mb-4 line-clamp-2 flex-grow text-xs leading-relaxed">
              {description}
            </p>
            <div className="text-muted-foreground/60 mt-auto flex items-center gap-2 text-[11px]">
              <time>{formatDate(date, language)}</time>
              <span>&middot;</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {readTime}
              </span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link
      href={`/blog/${slug}`}
      className="group block min-w-0 overflow-visible whitespace-normal"
    >
      <article
        className={cn(
          "flex h-full flex-col overflow-hidden rounded-xl",
          "border-border/50 bg-card border shadow-sm",
          "transition-all duration-300",
          "hover:border-border dark:bg-foreground/[0.02] dark:hover:bg-foreground/[0.04] hover:bg-muted hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/25 dark:shadow-none",
          className
        )}
      >
        <div className="relative aspect-[16/9] overflow-hidden rounded-t-xl">
          <Image
            src={imageUrl}
            alt={title}
            fill
            className="blog-card-image object-cover"
            unoptimized
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </div>

        <div className="flex flex-grow flex-col p-6">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {tags?.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="text-[11px] font-semibold tracking-widest text-green-600 uppercase dark:text-green-400/70"
              >
                {tag}
              </span>
            ))}
          </div>

          <h2
            className="text-foreground group-hover:text-leaf-600 dark:group-hover:text-leaf-300 mb-3 text-xl font-extrabold transition-colors"
            style={{
              display: "block",
              overflow: "visible",
              textOverflow: "unset",
              whiteSpace: "normal",
              wordBreak: "normal",
              overflowWrap: "anywhere",
              lineHeight: "1.4",
            }}
          >
            {title}
          </h2>

          <p className="text-muted-foreground/70 mb-5 line-clamp-3 flex-grow text-sm leading-relaxed">
            {description}
          </p>

          <div className="border-border/30 mt-auto flex items-center justify-between border-t pt-4">
            <div className="text-muted-foreground/60 flex items-center gap-3 text-xs">
              <time>{formatDate(date, language)}</time>
              <span>&middot;</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {readTime}
              </span>
            </div>
            <span className="text-leaf-600 dark:text-leaf-400 dark:group-hover:text-leaf-400 inline-flex items-center gap-1.5 text-xs font-semibold transition-all group-hover:gap-2.5">
              {t("readArticle")}
              <ArrowRight className="h-3 w-3" />
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}
